import { Children, isValidElement, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { Tabs as AntTabs } from 'antd';

// Preserve the shadcn-style API (Tabs/List/Trigger/Content) on top of antd Tabs.

interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (v: string) => void;
  className?: string;
  children: ReactNode;
}

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

interface TabsListProps {
  children: ReactNode;
  className?: string;
}

const TRIGGER_TYPE_MARK = Symbol.for('aidu.tabs.trigger');
const CONTENT_TYPE_MARK = Symbol.for('aidu.tabs.content');
const LIST_TYPE_MARK = Symbol.for('aidu.tabs.list');

type MarkedComponent<P> = ((p: P) => ReactNode) & { __markType?: symbol };

const TabsTriggerImpl: MarkedComponent<TabsTriggerProps> = (() => null) as never;
TabsTriggerImpl.__markType = TRIGGER_TYPE_MARK;
export const TabsTrigger = TabsTriggerImpl as unknown as (p: TabsTriggerProps) => ReactNode;

const TabsContentImpl: MarkedComponent<TabsContentProps> = (() => null) as never;
TabsContentImpl.__markType = CONTENT_TYPE_MARK;
export const TabsContent = TabsContentImpl as unknown as (p: TabsContentProps) => ReactNode;

const TabsListImpl: MarkedComponent<TabsListProps> = ((props) => <>{props.children}</>) as never;
TabsListImpl.__markType = LIST_TYPE_MARK;
export const TabsList = TabsListImpl as unknown as (p: TabsListProps) => ReactNode;

function findMarked(children: ReactNode, mark: symbol): ReactElement[] {
  const out: ReactElement[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const t = child.type as MarkedComponent<unknown> | undefined;
    if (t && t.__markType === mark) {
      out.push(child);
    } else if (child.props && (child.props as { children?: ReactNode }).children !== undefined) {
      out.push(...findMarked((child.props as { children?: ReactNode }).children, mark));
    }
  });
  return out;
}

export function Tabs({ defaultValue, value, onValueChange, className, children }: TabsProps) {
  const triggers = useMemo(() => findMarked(children, TRIGGER_TYPE_MARK), [children]);
  const contents = useMemo(() => findMarked(children, CONTENT_TYPE_MARK), [children]);

  const initial =
    value ??
    defaultValue ??
    (triggers[0]?.props as TabsTriggerProps | undefined)?.value ??
    (contents[0]?.props as TabsContentProps | undefined)?.value ??
    '';
  const [internal, setInternal] = useState(initial);
  const active = value ?? internal;

  const items = triggers.map((trigger) => {
    const tProps = trigger.props as TabsTriggerProps;
    const content = contents.find((c) => (c.props as TabsContentProps).value === tProps.value);
    return {
      key: tProps.value,
      label: tProps.children,
      disabled: tProps.disabled,
      children: content ? (content.props as TabsContentProps).children : null,
    };
  });

  return (
    <AntTabs
      activeKey={active}
      onChange={(k) => {
        setInternal(k);
        onValueChange?.(k);
      }}
      items={items}
      className={className}
    />
  );
}
